import { Auth } from 'aws-amplify';
import appConfig from '../config/app-config';
import { ErrorField } from '../types/ErrorField';

const API = appConfig.apis.personApiEnv;
const GEO = appConfig.apis.geolocationApiEnv;
const FINANCIAL_REPRESENTATIVE_PROFILE = 'FINANCIAL_REPRESENTATIVE';
const CANDIDATE_PAGE_SIZE = 20;

const getHeaders = async (): Promise<Record<string, string>> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    return {
        'Content-Type': 'application/json',
        'x-api-key': API.key,
        'Authorization': `Bearer ${token}`,
    };
};

export interface PersonCityState {
    city: string;
    state: string;
}

export interface PersonFullAddress {
    street: string;
    number: string;
    complement: string;
    district: string;
    city: string;
    state: string;
    zip_code: string;
    ibge_code: string;
}

export interface IndividualPerson {
    uuid: string;
    name: string;
    socialId: string;
    email: string;
    mobile: string;
}

export interface LegalPerson {
    personId: string;
    legalName: string;
    govTaxId: string;
}

export interface Signatory {
    individualUuid: string;
    fullName: string;
    socialId: string;
    email: string;
    mobile: string;
    position: string;
    isActive: boolean;
    allowedOperations: Array<{ operation: string; representationFormat: string } | string>;
    signAsEntries: { signAs: string; useDigitalCertificate: boolean }[];
    proxyExpirationDate?: string | null;
    proxyAllowedValue?: number | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    lastVerifiedAt?: string | null;
}

export interface PersonAddress {
    id: number;
    zipcode: string | null;
    streetType: string | null;
    street: string | null;
    number: string | null;
    complement: string | null;
    district: string | null;
    cityId: number | null;
}

export interface PersonSearchResult {
    uuid: string;                    // person aggregate id (→ billing external_id)
    personType: 'INDIVIDUAL' | 'LEGAL_PERSON';
    name: string;
    alias: string | null;
    contacts: { type: string; content: string }[];
    documents: { type: string; number: string }[];
    profiles?: string[];
}

export interface LegalPersonSearchResult {
    personId: string;
    legalName: string;
    fantasyName: string;
    govTaxId: string;
    govTaxIdFormatted: string;
}

export interface PersonByDocument {
    uuid: string;          // personId
    name: string;
    personType: 'INDIVIDUAL' | 'LEGAL_PERSON';
    document: string | null;
}

export interface PersonById {
    personId: string;
    name: string;
    personType: 'INDIVIDUAL' | 'LEGAL_PERSON';
    document: string | null;
}

export type ProfileType =
    | 'SIGNATORY'
    | 'CONSENTING_PARTY'
    | 'FINANCIAL_REPRESENTATIVE'
    | 'RECIPIENT'
    | 'GUARANTOR-RETAIL'
    | 'GUARANTOR-WHOLESALE'
    | 'ISSUER'
    | 'DRAWEE'
    | 'INTERNAL_CREDITOR'
    | 'EXTERNAL_CREDITOR';

export interface LegalPersonProfileItem {
    profile_type: ProfileType;
    profile_dict: Record<string, unknown> | null;
}

export interface LegalPersonPartnerData {
    address_one: string;
    address_number: string;
    address_two: string;
    address_detail: string;
    postal_code: string;
    city_id: string | number | null;
    guarantee_percentage: number | string;
}

export interface LegalPersonProfilesRequest {
    profiles: LegalPersonProfileItem[];
    partner_data?: LegalPersonPartnerData;
}

export interface FinancialRepresentative {
    individualUuid: string;
    socialId: string;
    name: string;
    phone: string | null;
    email: string | null;
}

export interface LegalPersonUpdateResult {
    financialRepresentatives?: FinancialRepresentative[];
}

export type LegalPersonUpdateError = Error & { fieldErrors: ErrorField[] };

export interface RegistrationNotification {
    legalPersonUuid: string;
    govTaxId: string;
    legalName: string;
    notifiedBy: string;
    notifiedAt: string;
    notifiedAtFormatted: string;
    eventId: string;
}

/**
 * Builds the POST /legal-persons/{id}/profiles body with only the profiles
 * that are selected and not yet persisted, each with an empty profile_dict.
 */
export const buildNewProfilesRequest = (selected: string[], persisted: string[]): LegalPersonProfilesRequest => ({
    profiles: selected
        .filter(profileType => !persisted.includes(profileType))
        .map(profileType => ({ profile_type: profileType as ProfileType, profile_dict: {} })),
});

export interface GuarantorRetailRequestForm {
    guaranteePercentage?: number | string;
    score?: number | string;
    limit?: number | string;
    alias?: string;
    logoUrl?: string;
    streetType?: string;
    street?: string;
    number?: string;
    district?: string;
    complement?: string;
    zipcode?: string;
    cityId?: string | number;
}

/**
 * Builds the "Salvar" (Aval. varejo) body: the full GUARANTOR-RETAIL profile_dict plus partner_data.
 */
export const buildGuarantorRetailProfilesRequest = (form: GuarantorRetailRequestForm): LegalPersonProfilesRequest => ({
    profiles: [
        {
            profile_type: 'GUARANTOR-RETAIL',
            profile_dict: {
                sign_as: 'Avalista',
                guarantor_data: {
                    guarantee_percentage: form.guaranteePercentage || 0,
                    score: form.score || 0,
                    credit_limit: form.limit || 0,
                    alias: form.alias || '',
                    logo_url: form.logoUrl || '',
                },
                use_digital_certificate: false,
            },
        },
    ],
    partner_data: {
        address_one: `${form.streetType || ''} ${form.street || ''}`.trim(),
        address_number: form.number || '',
        address_two: form.district || '',
        address_detail: form.complement || '',
        postal_code: form.zipcode?.replace(/\D/g, '') || '',
        city_id: form.cityId || null,
        guarantee_percentage: form.guaranteePercentage || 0,
    },
});

const toFieldError = (error: { fieldName?: string; field?: string; message?: string }): ErrorField => ({
    fieldName: error.fieldName ?? error.field ?? '',
    message: error.message ?? '',
});

const toLegalPersonUpdateError = (body: any, fallbackMessage: string): LegalPersonUpdateError => {
    const error = new Error(body?.error || body?.message || fallbackMessage) as LegalPersonUpdateError;
    error.fieldErrors = Array.isArray(body?.errors) ? body.errors.map(toFieldError) : [];
    return error;
};

export const personApi = {
    /**
     * Upserts legal person profiles (contract: services/contracts/legal-person-profiles-request.schema.json).
     */
    saveLegalPersonProfiles: async (personId: string, request: LegalPersonProfilesRequest): Promise<{ profiles: unknown[] }> => {
        const res = await fetch(`${API.address}/legal-persons/${personId}/profiles`, {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(request),
        });
        if (!res.ok) {
            const errData = await res.json().catch(() => null);
            throw new Error(errData?.message || 'Erro ao salvar perfis');
        }
        return res.json();
    },

    /**
     * Tells the legal team that the PJ registration is up to date
     * (contract: services/contracts/legal-person-registration-notification-response.schema.json).
     */
    notifyRegistrationUpdated: async (personId: string): Promise<RegistrationNotification> => {
        const res = await fetch(`${API.address}/legal-persons/${personId}/registration-notifications`, {
            method: 'POST',
            headers: await getHeaders(),
        });
        if (!res.ok) {
            const errData = await res.json().catch(() => null);
            throw new Error(errData?.errors?.[0]?.message || errData?.message || 'Erro ao enviar a notificação');
        }
        const body = await res.json();
        return body.data;
    },

    searchLegalPersons: async (query: string, signal?: AbortSignal): Promise<LegalPersonSearchResult[]> => {
        const stripped = query.replace(/[.\-/]/g, '').toUpperCase();
        const hasConsecutiveDigits = /\d{2,}/.test(stripped);
        const hasNoSpaces = !/\s/.test(stripped);
        const isCnpjSearch = hasConsecutiveDigits && hasNoSpaces && stripped.length <= 14;
        const queryParam = isCnpjSearch
            ? `govTaxId=${encodeURIComponent(stripped)}`
            : `legalName=${encodeURIComponent(query)}`;
        const res = await fetch(
            `${API.address}/legal-persons/page-list?${queryParam}&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders(), signal }
        );
        if (!res.ok) {
            const errData = await res.json().catch(() => null);
            const detail = errData?.message || `status ${res.status}`;
            throw new Error(`Erro ao buscar cedente: ${detail}`);
        }
        const data = await res.json();
        return (data.list || []).slice(0, 6);
    },

    /**
     * Search people by name, email or CPF.
     * - Query contains '@' → searches by email parameter
     * - Otherwise → searches by name parameter (Person API matches name OR socialId)
     * Returns all matches (including those without email).
     */
    searchPeople: async (query: string, signal?: AbortSignal): Promise<IndividualPerson[]> => {
        const clean = query.trim();
        if (!clean) return [];

        let param: string;
        if (clean.includes('@')) {
            param = `email=${encodeURIComponent(clean)}&name=&mobile=`;
        } else {
            const stripped = clean.replace(/\D/g, '');
            const isNumeric = /^\d+$/.test(stripped) && stripped.length >= 3;
            const nameValue = isNumeric ? stripped : clean;
            param = `name=${encodeURIComponent(nameValue)}&email=&mobile=`;
        }

        const res = await fetch(
            `${API.address}/individual/page-list?${param}&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders(), signal }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.list || [];
    },

    searchByNameOrCpf: async (query: string): Promise<IndividualPerson[]> => {
        const clean = query.trim();
        if (!clean) return [];
        const isNumeric = /^\d+$/.test(clean.replace(/\D/g, '')) && clean.replace(/\D/g, '').length >= 3;
        const param = isNumeric ? `name=${clean.replace(/\D/g, '')}` : `name=${encodeURIComponent(clean)}`;
        const res = await fetch(
            `${API.address}/individual/page-list?${param}&email=&mobile=&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return (data.list || []).filter((p: any) => p.email);
    },

    // Busca consolidada por CPF ou CNPJ (endpoint /person/all/<doc> — vw_person_search,
    // detecta documento por cpf/cnpj; a variante /search/all busca só por nome).
    // O path duplica "/person": a base (API.address) já é .../person e o serviço person
    // define as rotas como /person/all/... com strip_prefixes=["/person"].
    searchByDocument: async (doc: string): Promise<PersonSearchResult | null> => {
        const clean = doc.replace(/\D/g, '');
        if (clean.length !== 11 && clean.length !== 14) return null;
        const res = await fetch(`${API.address}/person/all/${clean}`, { headers: await getHeaders() });
        if (!res.ok) return null;
        const data = await res.json();
        const list: PersonSearchResult[] = data.results || [];
        return list.find(p => p.documents?.some(d => (d.number || '').replace(/\D/g, '') === clean)) || list[0] || null;
    },

    // Endereços de um person (PF ou PJ). Mesmo padrão de path duplicado "/person".
    getAddresses: async (personId: string): Promise<PersonAddress[]> => {
        const res = await fetch(`${API.address}/person/${personId}/addresses`, { headers: await getHeaders() });
        if (!res.ok) return [];
        const data = await res.json();
        return data.addresses || [];
    },

    searchIndividualByCpf: async (cpf: string): Promise<IndividualPerson | null> => {
        const clean = cpf.replace(/\D/g, '');
        if (clean.length !== 11) return null;
        const res = await fetch(
            `${API.address}/individual/page-list?name=${clean}&email=&mobile=&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const list: any[] = data.list || [];
        return list.find((p: any) => p.socialId?.replace(/\D/g, '') === clean) || null;
    },

    searchLegalPersonByCnpj: async (cnpj: string): Promise<LegalPerson | null> => {
        const clean = cnpj.replace(/\D/g, '');
        if (clean.length !== 14) return null;
        const res = await fetch(
            `${API.address}/legal-persons/page-list?govTaxId=${encodeURIComponent(clean)}&legalName=&page=0&size=10&sort=id,DESC`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const list: any[] = data.list || [];
        return list.find((p: any) => p.govTaxId?.replace(/\D/g, '') === clean) || null;
    },

    /**
     * Active Financial Representatives of the legal person (AF-4128); rejects when the response is
     * not ok (contract: services/contracts/legal-person-financial-representatives-response.schema.json).
     */
    getFinancialRepresentatives: async (legalPersonUuid: string): Promise<FinancialRepresentative[]> => {
        const res = await fetch(
            `${API.address}/legal-persons/${legalPersonUuid}/financial-representatives`,
            { headers: await getHeaders() }
        );
        if (!res.ok) throw new Error(`Erro ao carregar os representantes financeiros (${res.status})`);
        const body = await res.json();
        return body.data || [];
    },

    /**
     * Individuals with the FINANCIAL_REPRESENTATIVE profile matching a name or CPF (AF-4128);
     * an empty list when the response is not ok (same contract as getFinancialRepresentatives).
     */
    searchFinancialRepresentativeCandidates: async (term: string, signal?: AbortSignal): Promise<FinancialRepresentative[]> => {
        const res = await fetch(
            `${API.address}/individuals?filter[profileType]=${FINANCIAL_REPRESENTATIVE_PROFILE}&filter[term][like]=${encodeURIComponent(term)}&page=1&size=${CANDIDATE_PAGE_SIZE}`,
            { headers: await getHeaders(), signal }
        );
        if (!res.ok) return [];
        const body = await res.json();
        return body.data || [];
    },

    /**
     * PUT /legal-persons/{id} of the "Dados Cadastrais" tab. Rejects with a LegalPersonUpdateError whose
     * fieldErrors read fieldName (new errors) or field (legacy 409).
     */
    updateLegalPerson: async (personUuid: string, payload: object): Promise<LegalPersonUpdateResult> => {
        const res = await fetch(`${API.address}/legal-persons/${personUuid}`, {
            method: 'PUT',
            headers: await getHeaders(),
            body: JSON.stringify(payload),
        });
        const body = await res.json().catch(() => null);
        if (!res.ok) throw toLegalPersonUpdateError(body, `Erro ao salvar dados básicos (${res.status})`);
        return body || {};
    },

    getSignatories: async (legalPersonUuid: string): Promise<Signatory[]> => {
        const res = await fetch(
            `${API.address}/legal-persons/${legalPersonUuid}/signatories`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.signatories || [];
    },

    /**
    /**
     * Resolves the first registered address of a person into city + state.
     * Falls back to the geolocation service when the address only carries a cityId.
     */
    getPersonAddress: async (personId: string): Promise<PersonCityState | null> => {
        if (!personId || personId === '00000000-0000-0000-0000-000000000000') return null;
        const res = await fetch(
            `${API.address}/person/${personId}/addresses`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const addresses = data.addresses || data || [];
        if (!Array.isArray(addresses) || addresses.length === 0) return null;
        const addr = addresses[0];

        let city = addr.city || '';
        let state = addr.state || addr.province || '';

        if (addr.cityId && (!city || !state)) {
            try {
                const cityRes = await fetch(
                    `${GEO.address}/cities/${addr.cityId}?fields=id,name,province`,
                    { headers: { 'x-api-key': GEO.key } }
                );
                if (cityRes.ok) {
                    const cityData = await cityRes.json();
                    if (!city) city = cityData.name || '';
                    if (!state) state = cityData.province?.initials || cityData.province?.abbreviation || cityData.province || '';
                }
            } catch { /* ignore geolocation errors, return what we have */ }
        }

        return { city, state };
    },

    /**
     * Resolves the first registered address of a person into a full address
     * (street, number, complement, district, city, state, zip, ibge).
     * Falls back to the geolocation service when the address only carries a cityId.
     */
    getPersonFullAddress: async (personId: string): Promise<PersonFullAddress | null> => {
        if (!personId || personId === '00000000-0000-0000-0000-000000000000') return null;
        const res = await fetch(
            `${API.address}/person/${personId}/addresses`,
            { headers: await getHeaders() }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const addresses = data.addresses || data || [];
        if (!Array.isArray(addresses) || addresses.length === 0) return null;
        const addr = addresses[0];

        let city = addr.city || '';
        let state = addr.state || addr.province || '';
        let ibgeCode = addr.ibgeCode || addr.ibge_code || '';

        if (addr.cityId && (!city || !state || !ibgeCode)) {
            try {
                const cityRes = await fetch(
                    `${GEO.address}/cities/${addr.cityId}?fields=id,name,ibge,province`,
                    { headers: { 'x-api-key': GEO.key } }
                );
                if (cityRes.ok) {
                    const cityData = await cityRes.json();
                    if (!city) city = cityData.name || '';
                    if (!state) state = cityData.province?.initials || cityData.province?.abbreviation || cityData.province || '';
                    if (!ibgeCode) ibgeCode = cityData.ibge || cityData.ibgeCode || '';
                }
            } catch { /* ignore geolocation errors, return what we have */ }
        }

        return {
            street: addr.street || '',
            number: addr.number || '',
            complement: addr.complement || '',
            district: addr.district || addr.neighborhood || '',
            city,
            state,
            zip_code: addr.zipcode || addr.zip_code || '',
            ibge_code: ibgeCode,
        };
    },

    /**
     * Resolve uma pessoa (PF ou PJ) a partir do documento (CPF/CNPJ).
     * Usa GET /person/all/<doc>, que normaliza o documento no backend (com ou sem
     * máscara) e busca por igualdade de CPF/CNPJ. Só retorna a pessoa cujo documento
     * **casa exatamente** o digitado (normalizado); caso contrário, null. Sem fallback
     * para "primeiro da lista": vincular a pessoa errada a um caso jurídico seria um erro
     * grave e silencioso — melhor devolver null e a UI oferecer contraparte externa.
     * O `uuid` do resultado é o personId.
     */
    findPersonByDocument: async (
        document: string,
        signal?: AbortSignal,
    ): Promise<PersonByDocument | null> => {
        const clean = document.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        if (clean.length !== 11 && clean.length !== 14) return null;

        const res = await fetch(
            `${API.address}/person/all/${encodeURIComponent(clean)}`,
            { headers: await getHeaders(), signal },
        );
        if (!res.ok) return null;

        const data = await res.json();
        const results: any[] = data.results || [];
        const matchesDocument = (r: any) =>
            (r.documents || []).some(
                (d: any) => (d.number || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase() === clean,
            );

        const match = results.find(matchesDocument);
        if (!match) return null;

        const doc = (match.documents || []).find(
            (d: any) => (d.number || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase() === clean,
        );
        return {
            uuid: match.uuid,
            name: match.name,
            personType: match.personType,
            document: doc?.number || null,
        };
    },

    /**
     * Resolve nome + documento a partir do personId (UUID), sem saber se é PF ou PJ.
     * Não há endpoint único no `person`: tenta o individual (PF); em 404, cai para o
     * legal-person (PJ). Retorna null se não achar em nenhum dos dois. Usado para exibir
     * nome/documento na lista de partes sem copiar o dado no recovery (ADR 001).
     */
    resolvePersonById: async (
        personId: string,
        signal?: AbortSignal,
    ): Promise<PersonById | null> => {
        const headers = await getHeaders();

        // /individual/<id>/page-form é o endpoint batido para PF por UUID (usado por
        // IndividualPageEdit, IndividualFormConsentingParty etc.). Evita a rota "nua"
        // /individual/<id>, que colide com /individual/<socialId> no resolver do person.
        const individual = await fetch(
            `${API.address}/individual/${personId}/page-form`,
            { headers, signal },
        );
        if (individual.ok) {
            const data = await individual.json();
            return {
                personId,
                name: data.name || '',
                personType: 'INDIVIDUAL',
                document: data.socialId || null,
            };
        }

        const legal = await fetch(
            `${API.address}/legal-persons/${personId}/page-form`,
            { headers, signal },
        );
        if (legal.ok) {
            const data = await legal.json();
            return {
                personId,
                name: data.legalName || data.fantasyName || '',
                personType: 'LEGAL_PERSON',
                document: data.govTaxId || null,
            };
        }

        return null;
    },
};
